import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { UseMutationResult } from '@tanstack/react-query';
import { logger } from '@/lib/utils/logger';
import type { UserSession, TelegramAuthVerifyRequest } from '@/lib/server-fns/auth-fns';
import {
  navigateToPostLoginDestination,
  resolvePostLoginDestination,
} from '@/lib/auth/route-auth-decisions';

/**
 * Resolve an untrusted post-login `redirect` param to a safe same-origin
 * in-app path. Rejects cross-origin, protocol-relative (`//evil.com`), and
 * dangerous-scheme (`javascript:`, `data:`) values by falling back to /home.
 *
 * TanStack `navigate` is internal-only, so this is defense-in-depth — but we
 * never want to return a raw attacker-controlled string, and a future change
 * to window.location would otherwise turn this into a real open redirect.
 */
export function safeRedirect(
  raw: string | undefined,
  origin: string = window.location.origin
): string {
  const fallback = '/home';
  if (!raw) return fallback;
  try {
    const url = new URL(raw, origin);
    if (url.origin !== origin) return fallback;
    if (!url.pathname.startsWith('/')) return fallback;
    return url.pathname + url.search + url.hash;
  } catch {
    return fallback;
  }
}

interface UseUrlAuthEffectProps {
  search: {
    auth?: string;
    code?: string;
    redirect?: string;
  };
  allowCookies: boolean;
  verifyAuth: UseMutationResult<UserSession, Error, TelegramAuthVerifyRequest, unknown>;
  setAuthCode: (code: string | null) => void;
  setChallengeCode: (code: string) => void;
  setStep: (step: 'init' | 'challenge') => void;
  setError: (error: string | null) => void;
  setShakeCheckboxes: (shake: boolean) => void;
}

/**
 * Read magic-link credentials fragment-first (#auth=...&code=...), with the
 * legacy query-string form (?auth=...&code=...) as a bounded migration
 * fallback. Fragments never leave the browser, so new links cannot leak into
 * server logs, Referer headers, or history (issue #606).
 */
function readMagicLinkCredentials(search: UseUrlAuthEffectProps['search']): {
  auth?: string;
  code?: string;
} {
  if (typeof window === 'undefined') {
    return search;
  }
  // ?? guards: some hosts (jsdom-less tests, exotic embeds) expose a window
  // without a fully populated Location.
  const hash = window.location.hash ?? '';
  const searchString = window.location.search ?? '';
  if (hash.length > 1) {
    const params = new URLSearchParams(hash.slice(1));
    const auth = params.get('auth');
    const code = params.get('code');
    if (auth && code) return { auth, code };
  }
  if (searchString) {
    const params = new URLSearchParams(searchString);
    const auth = params.get('auth');
    const code = params.get('code');
    if (auth && code) return { auth, code };
  }
  return search;
}

/**
 * Scrub credentials from the URL with history.replaceState once they have
 * been read, so they never linger in history or get re-sent via Referer on
 * same-origin requests.
 */
function scrubUrlCredentials() {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const query = new URLSearchParams(url.search);
    let queryChanged = false;
    for (const key of ['auth', 'code']) {
      if (query.has(key)) {
        query.delete(key);
        queryChanged = true;
      }
    }
    if (queryChanged) url.search = query.toString();
    let hashChanged = false;
    if (url.hash.length > 1) {
      const hashParams = new URLSearchParams(url.hash.slice(1));
      for (const key of ['auth', 'code']) {
        if (hashParams.has(key)) {
          hashParams.delete(key);
          hashChanged = true;
        }
      }
      if (hashChanged) url.hash = hashParams.toString();
    }
    if (queryChanged || hashChanged) {
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }
  } catch {
    // Unparseable href: leave the URL untouched rather than break login.
  }
}

/**
 * Custom hook to handle URL-based authentication (magic link flow)
 *
 * This effect:
 * 1. Detects auth params in the URL fragment (#auth=...&code=...), falling
 *    back to the legacy query string (?auth=...&code=...)
 * 2. Scrubs the URL immediately after reading (history.replaceState)
 * 3. Auto-triggers verification if cookie consent given
 * 4. Handles verification errors with user-friendly messages
 * 5. Prevents infinite loops by tracking last processed auth token
 *
 * @param props - Configuration and state setters for auth flow
 */
export const useUrlAuthEffect = ({
  search,
  allowCookies,
  verifyAuth,
  setAuthCode,
  setChallengeCode,
  setStep,
  setError,
  setShakeCheckboxes,
}: UseUrlAuthEffectProps) => {
  const navigate = useNavigate();

  // Track last processed auth token to prevent infinite loops
  const lastHandledAuth = useRef<string | null>(null);

  useEffect(() => {
    const { auth, code } = readMagicLinkCredentials(search);
    if (auth && code) {
      // Prevent infinite loops / re-processing the same auth token
      if (lastHandledAuth.current === auth) {
        return;
      }
      lastHandledAuth.current = auth;

      // Credentials are no longer needed in the URL once read — scrub them so
      // they cannot linger in history or be re-sent via Referer.
      scrubUrlCredentials();

      logger.info('[Login]', 'Detected auth params in URL, auto-verifying...');

      // Decode URL parameters (handles % encoding)
      let decodedAuth = auth;
      let decodedCode = code;

      try {
        decodedAuth = decodeURIComponent(auth);
        decodedCode = decodeURIComponent(code);
      } catch {
        try {
          decodedAuth = decodeURI(auth);
          decodedCode = decodeURI(code);
        } catch {
          decodedAuth = auth;
          decodedCode = code;
        }
      }

      decodedAuth = decodedAuth.trim();
      decodedCode = decodedCode.trim();

      setAuthCode(decodedAuth);
      setChallengeCode(decodedCode);
      setStep('challenge');

      // Small delay to ensure state is set before verification
      setTimeout(() => {
        // Enforce cookie consent
        if (!allowCookies) {
          logger.info('[Login]', 'Cookie consent not given, pausing auto-verification');
          setShakeCheckboxes(true);
          setTimeout(() => setShakeCheckboxes(false), 500);
          return;
        }

        // Auto-trigger verification
        verifyAuth.mutate(
          {
            auth_code: decodedAuth,
            challenge_code: decodedCode,
          },
          {
            onSuccess: () => {
              const destination = resolvePostLoginDestination(
                search.redirect,
                window.location.origin,
                safeRedirect(search.redirect)
              );
              navigateToPostLoginDestination(destination, {
                assign: (url) => window.location.assign(url),
                navigate: (to) => navigate({ to, replace: true }),
              });
            },
            onError: (err) => {
              // SECURITY (issue #606): redacted identifiers only — never the
              // decoded credentials themselves.
              logger.error('[Login]', 'Magic link verification failed', {
                error: err,
                authCodeLength: decodedAuth.length,
                challengeCodeLength: decodedCode.length,
                errorMessage: err instanceof Error ? err.message : String(err),
              });

              const errorMessage = err instanceof Error ? err.message : String(err);

              // If auth code not found/expired, allow manual retry
              if (
                errorMessage.includes('auth code not found') ||
                errorMessage.includes('expired')
              ) {
                setError(
                  "The magic link verification failed. The code is already filled in - please click 'Verify & Sign In' to try again, or start over."
                );
                setAuthCode(decodedAuth);
                setChallengeCode(decodedCode);
              } else {
                setError(errorMessage || 'Failed to verify. Please try again.');
              }
            },
          }
        );
      }, 100);
    }
    return undefined;
  }, [
    search.auth,
    search.code,
    navigate,
    verifyAuth,
    allowCookies,
    setAuthCode,
    setChallengeCode,
    setStep,
    setError,
    setShakeCheckboxes,
  ]);
};
